import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/use-stores';
import TableServer from '../../components/super/TableServer';
import {
    Block,
    FlexDiv,
    Heading2,
    TextLead,
    BigModal,
    ThemedButton,
} from '@awarego-ui/components';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { capitalizeFirst } from '../../utils/helpers';
import FilterMultiSelect from '../../components/super/audit/FilterMultiSelect';
import FilterSingleSelect from '../../components/super/audit/FilterSingleSelect';
import FilterTextInput from '../../components/super/audit/FilterTextInput';
import AdvancedSearchModal from '../../components/super/audit/AdvancedSearchModal';
import Link from '@mui/material/Link';
import { alpha } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';
import { List, ListItem, ListItemText } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Popover from '@mui/material/Popover';
import AuditModalContent from '../../components/super/AuditModalContent';
import Switch from '@material-ui/core/Switch';

const CTX_LABELS = {
    authType: 'Authentication type',
    authScope: 'Authentication scope',
    companyName: 'Company name',
    ip: 'IP',
    integration: 'Integration',
    requestId: 'Request ID',
    partnerPageSwitch: 'Switch from partner portal',
    switchSource: 'Switch origin url',
    webhookId: 'Webhook ID',
    apiKeyId: 'API Key ID',
    packageId: 'Package ID',
    appRequestId: 'App Request ID',
    assessmentId: 'Assessment ID',
    assessmentType: 'Assessment Type',
    superAdminAction: 'Super Admin Action',
    assessmentUniqKey: 'Assessment Uniq Key',
    assessmentActive: 'Assessment Active',
    trainingId: 'Training ID',
    trainingActive: 'Training Active',
    listId: 'List ID',
    listName: 'List Name',
    listMode: 'Mode',
    userEmail: 'User Email',
    companyIdSuperAdmin: 'Company ID Origin (Forced null - Super Admin)',
    subscriptionSource: 'Triggered from',
    linkGeneratedBy: 'Sign-In Link generated by',
    linkGeneratedFrom: 'Sign-In Link generated from',
    linkGeneratedAt: 'Sign-In Link generated at',
    impersonatedFrom: 'Impersonated from',
    impersonatedBy: 'Impersonated by',
    settingsCategory: 'Settings category',
    scope: 'Override scope',
    questionId: 'Question ID',
    questionName: 'Question name',
    overrideCount: 'Override count',
    languages: 'Languages',
    keysByLanguage: 'Keys by language',
    partnerId: 'Partner ID',
}

const SUPER_ADMIN_ACTIONS = [
    { name: 'Include', value: 'include' },
    { name: 'Exclude', value: 'exclude' },
    { name: 'Only', value: 'only' },
]

function AuditLog() {
    const { superStore, authStore } = useStores();
    const { timeZone } = authStore;
    const {
        operationOptions,
        subjectOptions,
        detailsKeysOptions,
        contextKeysOptions,
    } = superStore;
    const rowsPerPage = superStore.auditQueryContext.n;

    const [ctxAnchor, setCtxAnchor] = useState(null);
    const ctxOpen = Boolean(ctxAnchor);
    const openCtx  = (e) => setCtxAnchor(e.currentTarget);
    const closeCtx = () => setCtxAnchor(null);
    const [showRawContext, setShowRawContext] = useState(false);

    const [selectedEntry, setSelectedEntry] = useState(null);
    const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);

    useEffect(() => {
        setShowRawContext(false);
    }, [selectedEntry]);

    const makeFilterHandler = (key) => (val) =>
        superStore.applyAuditFilter(key, val);

    const onChangePagingParams = useCallback(
        (pageIdx, pageSize) => {
            superStore.setAuditPagination(pageIdx, pageSize);
        },
        [superStore],
    );

    const onClearFilters = () => superStore.clearAuditFilters();

    const handleAdvancedFiltersApply = ({ detailsFilters, contextFilters }) => {
        superStore.applyAuditFilter('detailsFilters', detailsFilters);
        superStore.applyAuditFilter('contextFilters', contextFilters);
    };

    useEffect(() => {
        superStore._loadAuditsRaw({});
    }, []);

    const columns = useMemo(
        () => [
            {
                Header: 'Action ID',
                accessor: 'id',
                Cell: (x) => `#${x.value}`,
            },
            {
                Header: 'Company',
                id: 'companyId',
                accessor: (row) => `[${row.companyId}] - ${row.companyName || ''}`,
                Cell: (cell) => {
                    const orig = cell.row.original || {};
                    const idPart = `[${orig.companyId}]`;
                    const namePart = orig.companyName ? ` - ${orig.companyName}` : '';
                    const text = `${idPart}${namePart}`;
                    const isMatch = Number(orig.companyExact) === 1;
                    return isMatch ? <span className="exact-match-badge">{text}</span> : <span>{text}</span>;
                },
            },
            {
                Header: 'Partner',
                id: 'accountId',
                accessor: (row) => `[${row.accountId}] - ${row.accountName || ''}`,
                Cell: (cell) => {
                    const orig = cell.row.original || {};
                    const idPart = `[${orig.accountId}]`;
                    const namePart = orig.accountName ? ` - ${orig.accountName}` : '';
                    const text = `${idPart}${namePart}`;
                    const isMatch = Number(orig.accountExact) === 1;
                    return isMatch ? <span className="exact-match-badge">{text}</span> : <span>{text}</span>;
                },
            },
            { Header: 'Author', accessor: 'author' },
            {
                Header: 'Date & time',
                accessor: 'created',
                Cell: (x) => moment(x.value).format('LLL'),
            },
            {
                Header: 'Operation',
                accessor: 'operation',
                Cell: (x) => capitalizeFirst(x.value),
            },
            {
                Header: 'Subject',
                accessor: 'subject',
                Cell: (x) => capitalizeFirst(x.value),
            },
            {
                Header: ' ',
                Cell: (x) => (
                    <Link onClick={() => setSelectedEntry(x.row.original)}>
                        <TextLead themed bold link>
                            View details
                        </TextLead>
                    </Link>
                ),
                disableSortBy: true,
            },
        ],
        []
    );

    const onChangeSort = useCallback(
        (sortBy) => superStore.setAuditSortBy(sortBy),
        [superStore],
    );

    const normalizeContext = (ctx) => {
        if (!ctx) return {};
        if (typeof ctx === 'object') return ctx;
        if (typeof ctx === 'string') {
            try {
                return JSON.parse(ctx);
            } catch (e) {
                return { value: ctx };
            }
        }
        return { value: String(ctx) };
    };

    return (
        <FlexDiv>
            <Block flexGrow="9" mr="20">
                <Heading2 mb={16} ml={36}>Audit log</Heading2>
                <FlexDiv mb={16} column ml={36}>
                    <FlexDiv gap={12}>
                        <FilterTextInput
                            placeholder="Company ID or name"
                            label="Company"
                            value={superStore.auditQueryContext.companyId || ''}
                            onChange={(val) => makeFilterHandler('companyId')(val)}
                            sx={{
                                minWidth: 240,
                                '& input::placeholder': {
                                    fontSize: 13,
                                    fontWeight: 700,
                                    lineHeight: '18px',
                                    color: alpha('#3b3c3b', 0.6),
                                    opacity: 1,
                                },
                            }}
                        />
                        <FilterTextInput
                            placeholder="Partner ID or name"
                            label="Partner"
                            value={superStore.auditQueryContext.accountId || ''}
                            onChange={(val) => makeFilterHandler('accountId')(val)}
                            sx={{
                                minWidth: 240,
                                '& input::placeholder': {
                                    fontSize: 13,
                                    fontWeight: 700,
                                    lineHeight: '18px',
                                    color: alpha('#3b3c3b', 0.6),
                                    opacity: 1,
                                },
                            }}
                        />
                        <FilterTextInput
                            placeholder="Author contains…"
                            label="Author"
                            value={superStore.auditQueryContext.author || ''}
                            onChange={(val) => makeFilterHandler('author')(val)}
                            sx={{
                                minWidth: 240,
                                '& input::placeholder': {
                                    fontSize: 13,
                                    fontWeight: 700,
                                    lineHeight: '18px',
                                    color: alpha('#3b3c3b', 0.6),
                                    opacity: 1,
                                },
                            }}
                        />
                        <FilterMultiSelect
                            label="Subject"
                            value={superStore.auditQueryContext.subject}
                            onChange={makeFilterHandler('subject')}
                            options={subjectOptions}
                            sx={{ width: 240 }}
                        />
                        <FilterMultiSelect
                            label="Operation"
                            value={superStore.auditQueryContext.operation}
                            onChange={makeFilterHandler('operation')}
                            options={operationOptions}
                            sx={{ width: 240 }}

                        />
                        <FilterSingleSelect
                            label="Super Admins Actions"
                            value={superStore.auditQueryContext.filterSuperAdmins}
                            onChange={makeFilterHandler('filterSuperAdmins')}
                            options={SUPER_ADMIN_ACTIONS}
                            sx={{ width: 240 }}
                        />
                    </FlexDiv>

                    <FlexDiv gap={12} mt={6}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DateTimePicker
                                hiddenLabel
                                value={
                                    superStore.auditQueryContext.fromDate
                                        ? moment(superStore.auditQueryContext.fromDate)
                                        : null
                                }
                                ampm={false}
                                dayOfWeekFormatter={(d) => `${d}`}
                                onChange={(v) =>
                                    makeFilterHandler('fromDate')(v?.toDate())
                                }
                                format={`DD/MM/YYYY HH:mm[${timeZone}]`}
                                views={['day', 'hours', 'minutes']}
                                slotProps={{
                                    textField: {
                                        placeholder: 'From',
                                        size: 'small',
                                        sx: {
                                            width: 240,
                                            '& input::placeholder': {
                                                fontSize: 13,
                                                fontWeight: 700,
                                                lineHeight: '18px',
                                                color: alpha('#3b3c3b', 0.6),
                                                opacity: 1,
                                            },
                                        },
                                    },
                                    actionBar: { actions: ['clear', 'accept'] },
                                }}
                            />

                            <DateTimePicker
                                hiddenLabel
                                value={
                                    superStore.auditQueryContext.toDate
                                        ? moment(superStore.auditQueryContext.toDate)
                                        : null
                                }
                                ampm={false}
                                dayOfWeekFormatter={(d) => `${d}`}
                                onChange={(v) =>
                                    makeFilterHandler('toDate')(v?.toDate())
                                }
                                format={`DD/MM/YYYY HH:mm[${timeZone}]`}
                                views={['day', 'hours', 'minutes']}
                                slotProps={{
                                    textField: {
                                        placeholder: 'To',
                                        size: 'small',
                                        sx: {
                                            width: 240,
                                            '& input::placeholder': {
                                                fontSize: 13,
                                                fontWeight: 700,
                                                lineHeight: '18px',
                                                color: alpha('#3b3c3b', 0.6),
                                                opacity: 1,
                                            },
                                        },
                                    },
                                    actionBar: { actions: ['clear', 'accept'] },
                                }}
                            />
                            <IconButton
                                sx={{height: 42, alignContent: 'center'}}
                                onClick={() => setAdvancedSearchOpen(true)}
                                aria-label="advanced search"
                            >
                                <TuneIcon />
                            </IconButton>
                            <IconButton
                                sx={{height: 42, alignContent: 'center'}}
                                onClick={onClearFilters}
                                aria-label="clear filters"
                            >
                                <ClearIcon />
                            </IconButton>
                        </LocalizationProvider>
                    </FlexDiv>
                </FlexDiv>

                <AdvancedSearchModal
                    open={advancedSearchOpen}
                    onClose={() => setAdvancedSearchOpen(false)}
                    detailsFilters={superStore.auditQueryContext.detailsFilters}
                    contextFilters={superStore.auditQueryContext.contextFilters}
                    detailsKeysOptions={detailsKeysOptions}
                    contextKeysOptions={contextKeysOptions}
                    onApply={handleAdvancedFiltersApply}
                />


                <TableServer
                    columns={columns}
                    data={superStore.audits.slice()}
                    defaultSortBy={'created'}
                    onChangeSort={onChangeSort}
                    hidePagination={superStore.auditPages <= 1}
                    rowsTotal={superStore.auditCount}
                    pagesTotal={superStore.auditPages}
                    loadingData={superStore.loadingAudits}
                    onChangePagingParams={onChangePagingParams}
                    initialPageSize={rowsPerPage}
                />
            </Block>

            {selectedEntry && (
                <BigModal
                    ariaHideApp={false}
                    isOpen={!!selectedEntry}
                    onRequestClose={() => setSelectedEntry(null)}
                    heading={
                        <FlexDiv alignCenter>
                            {selectedEntry.type === 'diff' ? 'Changes' : 'Details'}
                            {selectedEntry?.context && (
                                <>
                                    <IconButton size="small" sx={{ ml: 1 }} onClick={openCtx}>
                                        <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>

                                    <Popover
                                        open={ctxOpen}
                                        anchorEl={ctxAnchor}
                                        onClose={closeCtx}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'flex-start', padding: 8 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)' }}>Show raw</span>
                                                <Switch
                                                    size="small"
                                                    checked={showRawContext}
                                                    onChange={(e) => setShowRawContext(e.target.checked)}
                                                />
                                            </div>
                                        </div>

                                        <List dense sx={{ px: 2, py: 1, minWidth: 220 }}>
                                            {(() => {
                                                const ctx = normalizeContext(selectedEntry.context);
                                                if (showRawContext) {
                                                    return Object.entries(ctx).map(([k, v]) => (
                                                        <ListItem key={k} disablePadding>
                                                            <ListItemText
                                                                primary={k}
                                                                secondary={
                                                                    v === null || v === undefined
                                                                        ? String(v)
                                                                        : (typeof v === 'object' ? JSON.stringify(v) : String(v))
                                                                }
                                                            />
                                                        </ListItem>
                                                    ));
                                                }

                                                return Object.entries(CTX_LABELS).map(([k, label]) => {
                                                    if (ctx && k in ctx) {
                                                        const v = ctx[k];
                                                        return (
                                                            <ListItem key={k} disablePadding>
                                                                <ListItemText
                                                                    primary={label}
                                                                    secondary={
                                                                        v === null || v === undefined
                                                                            ? String(v)
                                                                            : (typeof v === 'object' ? JSON.stringify(v) : String(v))
                                                                    }
                                                                />
                                                            </ListItem>
                                                        );
                                                    }
                                                    return null;
                                                });
                                            })()}
                                        </List>

                                    </Popover>
                                </>
                            )}
                        </FlexDiv>
                    }
                    width={800}
                    modalContent={<AuditModalContent entry={selectedEntry} />}
                    modalFooterContent={
                        <ThemedButton
                            mode="secondary"
                            onClick={() => setSelectedEntry(null)}
                            text="Close"
                        />
                    }
                />
            )}
        </FlexDiv>
    );
}

export default observer(AuditLog);